import {
  Container,
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Input,
  Button,
  Spinner,
  useToast,
  IconButton,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Alert,
  AlertIcon,
  Divider,
  Code,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { DeleteIcon, RepeatIcon } from '@chakra-ui/icons';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useState, useEffect, useRef } from 'react';
import { Card } from '../../../components/Card';
import { useAuth } from '../../../contexts/AuthContext';
import { useAdmin } from './useAdmin';
import { Redirect } from 'react-router-dom';
import { Department } from '../types';
import { TestDataGenerator } from './TestDataGenerator';
import { httpsCallable } from 'firebase/functions';
import { functionsUsEast1 } from '../../../utils/init-firebase';

// Validation schema for the new metric name
const MetricNameSchema = Yup.object().shape({
  name: Yup.string()
    .min(3, 'Must be at least 3 characters')
    .max(100, 'Must be 100 characters or less')
    .required('Metric name is required'),
});

// Validation schema for the new department name
const DepartmentNameSchema = Yup.object().shape({
  name: Yup.string()
    .min(3, 'Must be at least 3 characters')
    .max(100, 'Must be 100 characters or less')
    .required('Department name is required'),
});

/**
 * Check if user has access to development/admin tools
 * Returns true if:
 * - Running on localhost, OR
 * - User email is in the allowed list (configure via environment variable or hardcoded)
 * 
 * To configure allowed emails in production:
 * 1. Add to .env: REACT_APP_DEV_TOOLS_ALLOWED_EMAILS=admin@example.com,dev@example.com
 * 2. Or hardcode specific emails in the allowedEmails array below
 */
function hasDevToolsAccess(currentUser: any): boolean {
  // Check if running on localhost
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return true;
  }

  // Check if user email is in allowed list
  // Option 1: Configure via environment variable (recommended)
  // Add to .env file: REACT_APP_DEV_TOOLS_ALLOWED_EMAILS=admin@example.com,dev@example.com
  const envAllowedEmails = process.env.REACT_APP_DEV_TOOLS_ALLOWED_EMAILS?.split(',').map(e => e.trim()) || [];
  
  // Option 2: Hardcode specific emails here (for production)
  const hardcodedAllowedEmails: string[] = [
  ];

  const allowedEmails = [...envAllowedEmails, ...hardcodedAllowedEmails];

  // Get user email from currentUser (Firebase user object)
  const userEmail = currentUser?.email?.toLowerCase() || currentUser?.user?.email?.toLowerCase();
  if (userEmail && allowedEmails.length > 0 && allowedEmails.some((email) => email.toLowerCase() === userEmail)) {
    return true;
  }

  return false;
}

/**
 * Admin Panel Page
 * Manages user access and system settings for the EOS Module.
 */
export function AdminPage() {
  const { currentUser, SuperAdmin } = useAuth();
  const { 
    metricNames, 
    departments,
    loading, 
    departmentsLoading,
    addMetricName, 
    deleteMetricName,
    addDepartment,
    deleteDepartment,
    checkDepartmentUsage,
    restoreDepartment,
    getDeletedDepartments,
  } = useAdmin();
  const toast = useToast();
  const [deletedDepartments, setDeletedDepartments] = useState<Department[]>([]);
  const [deletingDepartment, setDeletingDepartment] = useState<{ id: string; name: string } | null>(null);
  const [usageInfo, setUsageInfo] = useState<{ inUse: boolean; usage: { rocks: number; issues: number; vtos: number; l10Notes: number } } | null>(null);
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();
  const [loadingDeleted, setLoadingDeleted] = useState(false);

  const handleAddMetric = async (
    values: { name: string },
    { setSubmitting, resetForm }: any
  ) => {
    if (!currentUser?.uid) {
      toast({
        title: 'Error',
        description: 'You must be logged in.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setSubmitting(false);
      return;
    }

    try {
      await addMetricName(values.name);
      toast({
        title: 'Metric Name Added',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      resetForm();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to add metric name.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMetric = async (id: string) => {
    // This is a destructive action, so we must confirm.
    if (window.confirm('Are you sure you want to delete this metric name? This action cannot be undone.')) {
      try {
        await deleteMetricName(id);
        toast({
          title: 'Metric Name Deleted',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } catch (error) {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to delete metric name.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handleAddDepartment = async (
    values: { name: string },
    { setSubmitting, resetForm }: any
  ) => {
    if (!currentUser?.uid) {
      toast({
        title: 'Error',
        description: 'You must be logged in.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setSubmitting(false);
      return;
    }

    try {
      await addDepartment(values.name);
      toast({
        title: 'Department Added',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      resetForm();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to add department.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Load deleted departments
  useEffect(() => {
    const loadDeleted = async () => {
      try {
        setLoadingDeleted(true);
        const deleted = await getDeletedDepartments();
        setDeletedDepartments(deleted);
      } catch (error) {
        console.error('Error loading deleted departments:', error);
      } finally {
        setLoadingDeleted(false);
      }
    };
    loadDeleted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departments]); // Reload when departments change (getDeletedDepartments is stable)

  const handleDeleteDepartmentClick = async (id: string, name: string) => {
    try {
      // Check usage first
      const usage = await checkDepartmentUsage(id);
      setUsageInfo(usage);
      setDeletingDepartment({ id, name });
      onDeleteModalOpen();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to check department usage.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteDepartmentConfirm = async () => {
    if (!deletingDepartment) return;

    try {
      await deleteDepartment(deletingDepartment.id);
      toast({
        title: 'Department Deleted',
        description: 'Department has been soft deleted and can be restored.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      // Reload deleted departments
      const deleted = await getDeletedDepartments();
      setDeletedDepartments(deleted);
      onDeleteModalClose();
      setDeletingDepartment(null);
      setUsageInfo(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete department.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleRestoreDepartment = async (id: string, name: string) => {
    try {
      await restoreDepartment(id);
      toast({
        title: 'Department Restored',
        description: `${name} has been restored.`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      // Reload deleted departments
      const deleted = await getDeletedDepartments();
      setDeletedDepartments(deleted);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to restore department.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (!SuperAdmin) {
    return <Redirect to="/" />;
  }

  const showDevTools = hasDevToolsAccess(currentUser);

  return (
    <Container maxW="container.xl" py={8}>
      <Box mb={6}>
        <Heading size="lg" color="gray.900">
          EOS Admin Panel
        </Heading>
        <Text color="gray.600" mt={1}>
          Manage user access and system settings for the EOS Module.
        </Text>
      </Box>
      <Tabs isLazy colorScheme="blue" variant="enclosed-colored">
        <TabList>
          <Tab>Metric Management</Tab>
          <Tab>Department Management</Tab>
          {showDevTools && <Tab>Test Data Generator</Tab>}
          {showDevTools && <Tab>Data Migration</Tab>}
        </TabList>
        <TabPanels>
          {/* Panel 1: Metric Management */}
          <TabPanel px={0}>
            <Card>
              <Box p={4}>
                <Heading size="md" mb={2}>
                  Metric Names
                </Heading>
                <Text color="gray.600" fontSize="sm" mb={4}>
                  Manage predefined metric names that appear in the Add Metric dropdown.
                </Text>

                {/* Add Metric Form */}
                <Formik
                  initialValues={{ name: '' }}
                  validationSchema={MetricNameSchema}
                  onSubmit={handleAddMetric}
                >
                  {({ isSubmitting, errors, touched }) => (
                    <Form>
                      <HStack mb={6}>
                        <Field name="name">
                          {({ field }: any) => (
                            <Box flex="1">
                              <Input
                                {...field}
                                placeholder="Enter metric name..."
                                isInvalid={!!errors.name && touched.name}
                              />
                              {errors.name && touched.name && (
                                <Text color="red.500" fontSize="xs" mt={1}>
                                  {errors.name}
                                </Text>
                              )}
                            </Box>
                          )}
                        </Field>
                        <Button
                          colorScheme="blue"
                          type="submit"
                          isLoading={isSubmitting}
                        >
                          Add Metric Name
                        </Button>
                      </HStack>
                    </Form>
                  )}
                </Formik>

                {/* Metric List */}
                {loading ? (
                  <Flex justify="center" align="center" h="100px">
                    <Spinner />
                  </Flex>
                ) : (
                  <VStack align="stretch" spacing={3}>
                    {metricNames.map((metric) => (
                      <Flex
                        key={metric.id}
                        justify="space-between"
                        align="center"
                        p={4}
                        bg="gray.50"
                        borderRadius="md"
                        border="1px"
                        borderColor="gray.200"
                      >
                        <Text fontWeight="medium" color="gray.700">
                          {metric.name}
                        </Text>
                        <IconButton
                          aria-label="Delete metric"
                          icon={<DeleteIcon />}
                          colorScheme="red"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteMetric(metric.id)}
                        />
                      </Flex>
                    ))}
                  </VStack>
                )}
              </Box>
            </Card>
          </TabPanel>

          {/* Panel 2: Department Management */}
          <TabPanel px={0}>
            <Card>
              <Box p={4}>
                <Heading size="md" mb={2}>
                  Departments
                </Heading>
                <Text color="gray.600" fontSize="sm" mb={4}>
                  Manage departments for organizing accountability chart roles and team structure.
                </Text>

                {/* Add Department Form */}
                <Formik
                  initialValues={{ name: '' }}
                  validationSchema={DepartmentNameSchema}
                  onSubmit={handleAddDepartment}
                >
                  {({ isSubmitting, errors, touched }) => (
                    <Form>
                      <HStack mb={6}>
                        <Field name="name">
                          {({ field }: any) => (
                            <Box flex="1">
                              <Input
                                {...field}
                                placeholder="Enter department name..."
                                isInvalid={!!errors.name && touched.name}
                              />
                              {errors.name && touched.name && (
                                <Text color="red.500" fontSize="xs" mt={1}>
                                  {errors.name}
                                </Text>
                              )}
                            </Box>
                          )}
                        </Field>
                        <Button
                          colorScheme="blue"
                          type="submit"
                          isLoading={isSubmitting}
                        >
                          Add Department
                        </Button>
                      </HStack>
                    </Form>
                  )}
                </Formik>

                {/* Department List */}
                {departmentsLoading ? (
                  <Flex justify="center" align="center" h="100px">
                    <Spinner />
                  </Flex>
                ) : (
                  <VStack align="stretch" spacing={3}>
                    {departments.length === 0 ? (
                      <Text color="gray.500" fontStyle="italic" textAlign="center" py={4}>
                        No departments yet
                      </Text>
                    ) : (
                      departments.map((dept) => (
                        <Flex
                          key={dept.id}
                          justify="space-between"
                          align="center"
                          p={4}
                          bg="gray.50"
                          borderRadius="md"
                          border="1px"
                          borderColor="gray.200"
                        >
                          <Text fontWeight="medium" color="gray.700">
                            {dept.name}
                          </Text>
                          <IconButton
                            aria-label="Delete department"
                            icon={<DeleteIcon />}
                            colorScheme="red"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteDepartmentClick(dept.id, dept.name)}
                          />
                        </Flex>
                      ))
                    )}
                  </VStack>
                )}

                {/* Deleted Departments Section */}
                {deletedDepartments.length > 0 && (
                  <>
                    <Divider my={6} />
                    <Box>
                      <Heading size="sm" mb={3} color="gray.700">
                        Deleted Departments
                      </Heading>
                      <Text color="gray.600" fontSize="sm" mb={4}>
                        Restore deleted departments or permanently remove them.
                      </Text>
                      {loadingDeleted ? (
                        <Flex justify="center" align="center" h="50px">
                          <Spinner size="sm" />
                        </Flex>
                      ) : (
                        <VStack align="stretch" spacing={3}>
                          {deletedDepartments.map((dept) => (
                            <Flex
                              key={dept.id}
                              justify="space-between"
                              align="center"
                              p={4}
                              bg="red.50"
                              borderRadius="md"
                              border="1px"
                              borderColor="red.200"
                            >
                              <Text fontWeight="medium" color="gray.700" fontStyle="italic">
                                {dept.name}
                              </Text>
                              <IconButton
                                aria-label="Restore department"
                                icon={<RepeatIcon />}
                                colorScheme="green"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRestoreDepartment(dept.id, dept.name)}
                              />
                            </Flex>
                          ))}
                        </VStack>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </Card>
          </TabPanel>

          {/* Panel 3: Test Data Generator */}
          {showDevTools && (
            <TabPanel px={0}>
              <Card>
                <TestDataGenerator />
              </Card>
            </TabPanel>
          )}

          {/* Panel 4: Data Migration */}
          {showDevTools && (
            <TabPanel px={0}>
              <MigrationPanel />
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>

      {/* Delete Confirmation Modal with Usage Info */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Department</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>
              Are you sure you want to delete <strong>{deletingDepartment?.name}</strong>?
            </Text>
            {usageInfo && usageInfo.inUse && (
              <Alert status="warning" mb={4}>
                <AlertIcon />
                <Box>
                  <Text fontWeight="bold" mb={2}>
                    This department is currently in use:
                  </Text>
                  <VStack align="start" spacing={1} fontSize="sm">
                    {usageInfo.usage.rocks > 0 && (
                      <Text>• {usageInfo.usage.rocks} Rock(s)</Text>
                    )}
                    {usageInfo.usage.issues > 0 && (
                      <Text>• {usageInfo.usage.issues} Issue(s)</Text>
                    )}
                    {usageInfo.usage.vtos > 0 && (
                      <Text>• {usageInfo.usage.vtos} V/TO document(s)</Text>
                    )}
                    {usageInfo.usage.l10Notes > 0 && (
                      <Text>• {usageInfo.usage.l10Notes} L10 Note(s)</Text>
                    )}
                  </VStack>
                  <Text mt={2} fontSize="sm">
                    The department will be soft deleted and can be restored later.
                  </Text>
                </Box>
              </Alert>
            )}
            {usageInfo && !usageInfo.inUse && (
              <Alert status="info" mb={4}>
                <AlertIcon />
                This department is not currently in use. It will be soft deleted and can be restored later.
              </Alert>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteModalClose}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={handleDeleteDepartmentConfirm}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
}

/**
 * Migration Panel Component
 * Provides UI for running Supabase to Firebase migration functions
 */
function MigrationPanel() {
  const toast = useToast();
  const [auditLoading, setAuditLoading] = useState(false);
  const [testMigrationLoading, setTestMigrationLoading] = useState(false);
  const [fullMigrationLoading, setFullMigrationLoading] = useState(false);
  const [clearEOSLoading, setClearEOSLoading] = useState(false);
  const [fixActionItemsLoading, setFixActionItemsLoading] = useState(false);
  const [auditResult, setAuditResult] = useState<any>(null);
  const [migrationResult, setMigrationResult] = useState<any>(null);
  const [clearEOSResult, setClearEOSResult] = useState<any>(null);
  const [fixActionItemsResult, setFixActionItemsResult] = useState<any>(null);
  
  const { isOpen: isTestOpen, onOpen: onTestOpen, onClose: onTestClose } = useDisclosure();
  const { isOpen: isFullOpen, onOpen: onFullOpen, onClose: onFullClose } = useDisclosure();
  const { isOpen: isClearOpen, onOpen: onClearOpen, onClose: onClearClose } = useDisclosure();
  const { isOpen: isFixOpen, onOpen: onFixOpen, onClose: onFixClose } = useDisclosure();
  const testCancelRef = useRef<HTMLButtonElement>(null);
  const fullCancelRef = useRef<HTMLButtonElement>(null);
  const clearCancelRef = useRef<HTMLButtonElement>(null);
  const fixCancelRef = useRef<HTMLButtonElement>(null);

  const auditUsers = httpsCallable(functionsUsEast1, 'auditUsers');
  const testMigration = httpsCallable(functionsUsEast1, 'testMigration');
  const migrateSupabaseToFirebase = httpsCallable(functionsUsEast1, 'migrateSupabaseToFirebase');
  const clearEOSData = httpsCallable(functionsUsEast1, 'clearEOSData');
  const fixActionItemRockAssociations = httpsCallable(functionsUsEast1, 'fixActionItemRockAssociations');

  const handleAuditUsers = async () => {
    setAuditLoading(true);
    setAuditResult(null);
    try {
      const response = await auditUsers({});
      setAuditResult(response.data);
      toast({
        title: 'User Audit Completed',
        description: 'Check the results below',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('Error auditing users:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to audit users',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setAuditLoading(false);
    }
  };

  const handleTestMigration = async (dryRun: boolean = false) => {
    onTestClose();
    setTestMigrationLoading(true);
    setMigrationResult(null);
    try {
      const response = await testMigration({
        limit: 10,
        dryRun,
      });
      setMigrationResult(response.data);
      toast({
        title: dryRun ? 'Test Migration (Dry Run) Completed' : 'Test Migration Completed',
        description: 'Check the results below',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('Error running test migration:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to run test migration',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setTestMigrationLoading(false);
    }
  };

  const handleFullMigration = async (dryRun: boolean = false) => {
    onFullClose();
    setFullMigrationLoading(true);
    setMigrationResult(null);
    try {
      const response = await migrateSupabaseToFirebase({
        mode: 'full',
        dryRun,
        auditUsers: true,
        resumeFromCheckpoint: false,
      });
      setMigrationResult(response.data);
      toast({
        title: dryRun ? 'Full Migration (Dry Run) Completed' : 'Full Migration Completed',
        description: 'Check the results below',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('Error running full migration:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to run full migration',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setFullMigrationLoading(false);
    }
  };

  const handleClearEOSData = async () => {
    onClearClose();
    setClearEOSLoading(true);
    setClearEOSResult(null);
    try {
      const response = await clearEOSData({});
      setClearEOSResult(response.data);
      toast({
        title: 'EOS Data Cleared',
        description: 'All EOS collections have been cleared (users collection was NOT modified)',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('Error clearing EOS data:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to clear EOS data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setClearEOSLoading(false);
    }
  };

  const handleFixActionItems = async (dryRun: boolean = false) => {
    onFixClose();
    setFixActionItemsLoading(true);
    setFixActionItemsResult(null);
    try {
      const response = await fixActionItemRockAssociations({ dryRun });
      const result = response.data as {
        success?: boolean;
        actionItemsFixed?: number;
        todosFixed?: number;
        skipped?: number;
        errors?: Array<{ id?: string; source?: string; error?: string }>;
      };
      setFixActionItemsResult(result);
      toast({
        title: dryRun ? 'Fix Action Items (Dry Run) Completed' : 'Fix Action Items Completed',
        description: `Fixed ${result.actionItemsFixed || 0} action items and ${result.todosFixed || 0} todos`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('Error fixing action items:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to fix action items',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setFixActionItemsLoading(false);
    }
  };

  return (
    <Card>
      <Box p={4}>
        <Heading size="md" mb={2}>
          Supabase to Firebase Migration
        </Heading>
        <Text color="gray.600" fontSize="sm" mb={6}>
          Migrate data from Supabase to Firebase Firestore. Start with user audit, then test migration, then full migration.
        </Text>

        <VStack align="stretch" spacing={4}>
          {/* User Audit Section */}
          <Box p={4} bg="blue.50" borderRadius="md" border="1px" borderColor="blue.200">
            <Heading size="sm" mb={2} color="blue.900">
              1. Audit Users
            </Heading>
            <Text color="gray.700" fontSize="sm" mb={3}>
              Compare users between Supabase and Firestore (read-only). This will identify missing data points without modifying any users.
            </Text>
            <Button
              colorScheme="blue"
              onClick={handleAuditUsers}
              isLoading={auditLoading}
              loadingText="Auditing..."
            >
              Run User Audit
            </Button>
            {auditResult && (
              <Box mt={4} p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                <Text fontWeight="bold" mb={2}>Audit Results:</Text>
                <Code display="block" whiteSpace="pre-wrap" p={2} fontSize="xs" overflowX="auto">
                  {auditResult.report || JSON.stringify(auditResult, null, 2)}
                </Code>
              </Box>
            )}
          </Box>

          {/* Test Migration Section */}
          <Box p={4} bg="yellow.50" borderRadius="md" border="1px" borderColor="yellow.200">
            <Heading size="sm" mb={2} color="yellow.900">
              2. Test Migration
            </Heading>
            <Text color="gray.700" fontSize="sm" mb={3}>
              Migrate a small subset (10 records per collection) to validate the migration process. Always test before running a full migration.
            </Text>
            <HStack spacing={3}>
              <Button
                colorScheme="yellow"
                onClick={onTestOpen}
                isLoading={testMigrationLoading}
                loadingText="Migrating..."
              >
                Run Test Migration
              </Button>
              <Button
                colorScheme="yellow"
                variant="outline"
                onClick={() => handleTestMigration(true)}
                isLoading={testMigrationLoading}
                loadingText="Validating..."
              >
                Dry Run (Validate Only)
              </Button>
            </HStack>
            {migrationResult && (
              <Box mt={4} p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                <Text fontWeight="bold" mb={2}>Migration Results:</Text>
                <Code display="block" whiteSpace="pre-wrap" p={2} fontSize="xs" overflowX="auto">
                  {migrationResult.reportText || JSON.stringify(migrationResult, null, 2)}
                </Code>
              </Box>
            )}
          </Box>

          {/* Full Migration Section */}
          <Box p={4} bg="red.50" borderRadius="md" border="1px" borderColor="red.200">
            <Heading size="sm" mb={2} color="red.900">
              3. Full Migration
            </Heading>
            <Text color="gray.700" fontSize="sm" mb={3}>
              <strong>Warning:</strong> This will migrate all data from Supabase to Firestore. This is a long-running operation. Make sure you have tested the migration first.
            </Text>
            <HStack spacing={3}>
              <Button
                colorScheme="red"
                onClick={onFullOpen}
                isLoading={fullMigrationLoading}
                loadingText="Migrating..."
              >
                Run Full Migration
              </Button>
              <Button
                colorScheme="red"
                variant="outline"
                onClick={() => handleFullMigration(true)}
                isLoading={fullMigrationLoading}
                loadingText="Validating..."
              >
                Dry Run (Validate Only)
              </Button>
            </HStack>
          </Box>

          {/* Clear EOS Data Section */}
          <Box p={4} bg="purple.50" borderRadius="md" border="1px" borderColor="purple.200">
            <Heading size="sm" mb={2} color="purple.900">
              4. Clear All EOS Data
            </Heading>
            <Text color="gray.700" fontSize="sm" mb={3}>
              <strong>DANGER:</strong> This will permanently delete all EOS data from Firestore. The <strong>users collection will NOT be modified</strong>. This is useful for clearing data before a fresh migration.
            </Text>
            <Button
              colorScheme="purple"
              onClick={onClearOpen}
              isLoading={clearEOSLoading}
              loadingText="Clearing..."
              leftIcon={<DeleteIcon />}
            >
              Clear All EOS Data
            </Button>
            {clearEOSResult && (
              <Box mt={4} p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                <Text fontWeight="bold" mb={2}>Clear Results:</Text>
                <Code display="block" whiteSpace="pre-wrap" p={2} fontSize="xs" overflowX="auto">
                  {clearEOSResult.report || JSON.stringify(clearEOSResult, null, 2)}
                </Code>
              </Box>
            )}
          </Box>

          {/* Fix Action Item Rock Associations Section */}
          <Box p={4} bg="green.50" borderRadius="md" border="1px" borderColor="green.200">
            <Heading size="sm" mb={2} color="green.900">
              5. Fix Action Item Rock Associations
            </Heading>
            <Text color="gray.700" fontSize="sm" mb={3}>
              Fixes existing migrated action items that are missing rock associations. This updates action items from the <code>action_items</code> table (sets <code>rockId</code>) and todos from the <code>todos</code> table (sets <code>linkedRockId</code>). Also ensures all action items have the <code>dropped</code> field set.
            </Text>
            <HStack spacing={3}>
              <Button
                colorScheme="green"
                onClick={onFixOpen}
                isLoading={fixActionItemsLoading}
                loadingText="Fixing..."
                leftIcon={<RepeatIcon />}
              >
                Fix Action Items
              </Button>
              <Button
                colorScheme="green"
                variant="outline"
                onClick={() => handleFixActionItems(true)}
                isLoading={fixActionItemsLoading}
                loadingText="Validating..."
              >
                Dry Run (Validate Only)
              </Button>
            </HStack>
            {fixActionItemsResult && (
              <Box mt={4} p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                <Text fontWeight="bold" mb={2}>Fix Results:</Text>
                <VStack align="stretch" spacing={2} fontSize="sm">
                  <Text>
                    <strong>Action items fixed (rockId):</strong> {fixActionItemsResult.actionItemsFixed || 0}
                  </Text>
                  <Text>
                    <strong>Todos fixed (linkedRockId):</strong> {fixActionItemsResult.todosFixed || 0}
                  </Text>
                  <Text>
                    <strong>Skipped:</strong> {fixActionItemsResult.skipped || 0}
                  </Text>
                  {fixActionItemsResult.errors && fixActionItemsResult.errors.length > 0 && (
                    <Box>
                      <Text fontWeight="bold" mb={1}>Errors:</Text>
                      <Code display="block" whiteSpace="pre-wrap" p={2} fontSize="xs" overflowX="auto">
                        {JSON.stringify(fixActionItemsResult.errors, null, 2)}
                      </Code>
                    </Box>
                  )}
                </VStack>
              </Box>
            )}
          </Box>
        </VStack>

        {/* Test Migration Confirmation Dialog */}
        <AlertDialog
          isOpen={isTestOpen}
          leastDestructiveRef={testCancelRef}
          onClose={onTestClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Run Test Migration?
              </AlertDialogHeader>
              <AlertDialogBody>
                This will migrate 10 records from each collection in Supabase to Firestore. This is a test run to validate the migration process.
                <Alert status="warning" mt={4}>
                  <AlertIcon />
                  Make sure you have run the User Audit first and reviewed the results.
                </Alert>
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={testCancelRef} onClick={onTestClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="yellow"
                  onClick={() => handleTestMigration(false)}
                  ml={3}
                >
                  Run Test Migration
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        {/* Full Migration Confirmation Dialog */}
        <AlertDialog
          isOpen={isFullOpen}
          leastDestructiveRef={fullCancelRef}
          onClose={onFullClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Run Full Migration?
              </AlertDialogHeader>
              <AlertDialogBody>
                <Text mb={3}>
                  This will migrate <strong>all data</strong> from Supabase to Firestore. This is a long-running operation that may take several minutes or hours.
                </Text>
                <Alert status="error" mb={3}>
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">Warning:</Text>
                    <Text fontSize="sm">
                      • Make sure you have completed the User Audit and Test Migration first
                      <br />
                      • This operation cannot be easily undone
                      <br />
                      • The migration will use checkpoints and can be resumed if interrupted
                    </Text>
                  </Box>
                </Alert>
                <Text fontSize="sm" color="gray.600">
                  Are you sure you want to proceed?
                </Text>
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={fullCancelRef} onClick={onFullClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  onClick={() => handleFullMigration(false)}
                  ml={3}
                >
                  Run Full Migration
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        {/* Clear EOS Data Confirmation Dialog */}
        <AlertDialog
          isOpen={isClearOpen}
          leastDestructiveRef={clearCancelRef}
          onClose={onClearClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Clear All EOS Data?
              </AlertDialogHeader>
              <AlertDialogBody>
                <Text mb={3}>
                  This will <strong>permanently delete</strong> all EOS data from Firestore. This includes:
                </Text>
                <Box as="ul" mb={3} pl={4}>
                  <li>Rocks, Issues, Metrics</li>
                  <li>Action Items, Todos</li>
                  <li>Meetings, L10 Notes</li>
                  <li>AI Conversations</li>
                  <li>VTO Documents</li>
                  <li>And all other EOS collections</li>
                </Box>
                <Alert status="error" mb={3}>
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">CRITICAL WARNING:</Text>
                    <Text fontSize="sm">
                      • This operation <strong>CANNOT be undone</strong>
                      <br />
                      • The <strong>users collection will NOT be modified</strong>
                      <br />
                      • All EOS data will be permanently deleted
                      <br />
                      • Only use this before a fresh migration
                    </Text>
                  </Box>
                </Alert>
                <Text fontSize="sm" color="gray.600" fontWeight="bold">
                  Are you absolutely sure you want to proceed?
                </Text>
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={clearCancelRef} onClick={onClearClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="purple"
                  onClick={handleClearEOSData}
                  ml={3}
                  leftIcon={<DeleteIcon />}
                >
                  Clear All EOS Data
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        {/* Fix Action Items Confirmation Dialog */}
        <AlertDialog
          isOpen={isFixOpen}
          leastDestructiveRef={fixCancelRef}
          onClose={onFixClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Fix Action Item Rock Associations?
              </AlertDialogHeader>
              <AlertDialogBody>
                <Text mb={3}>
                  This will update existing migrated action items to fix missing rock associations:
                </Text>
                <Box as="ul" mb={3} pl={4}>
                  <li>Update action items from <code>action_items</code> table with correct <code>rockId</code></li>
                  <li>Update todos from <code>todos</code> table with correct <code>linkedRockId</code></li>
                  <li>Ensure all action items have <code>dropped: false</code> field set</li>
                </Box>
                <Alert status="info" mb={3}>
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">Note:</Text>
                    <Text fontSize="sm">
                      • This operation queries Supabase to find action items with rock associations
                      <br />
                      • Only action items missing or with incorrect associations will be updated
                      <br />
                      • This is safe to run multiple times (idempotent)
                      <br />
                      • Action items will appear in rock cards after this fix
                    </Text>
                  </Box>
                </Alert>
                <Text fontSize="sm" color="gray.600">
                  Do you want to proceed?
                </Text>
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={fixCancelRef} onClick={onFixClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="green"
                  onClick={() => handleFixActionItems(false)}
                  ml={3}
                  leftIcon={<RepeatIcon />}
                >
                  Fix Action Items
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Box>
    </Card>
  );
}